/* eslint-disable react/no-array-index-key */

import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Box, Flex, Button, Icon, Popover } from "orcs-design-system";
import parseHTMLToReact from "html-react-parser";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { useScrollToBottom } from "src/hooks/useScrollToBottom";
import {
  useWorkspace,
  useWorkspaceFeatureFlags,
} from "src/contexts/global/WorkspaceContext";
import { useAuth0 } from "src/contexts/auth0Provider";
import { useUserPerson } from "src/contexts/global/GlobalContext";
import { getName } from "src/util/person";
import { AIMessage } from "./AIMessage";
import { ToolMessage } from "./ToolMessageWithAgentCore";
import { createHtmlParsingOptions } from "./HTMLElementOverrides";
import { useAIChat } from "./context/AIChatContext";
import AIChatWelcome from "./AIChatWelcome";
import { PromptButton } from "./SuggestedPrompts";
import {
  extractFollowUpPrompts,
  stripFollowUpTags,
} from "./utils/extractFollowUpPrompts";
import MessageFeedback from "./MessageFeedback";

/**
 * Styled wrapper component for AI messages
 * @component
 * @example
 * <AIMessageWrapper>
 *   <AIMessage content="Hello world" />
 * </AIMessageWrapper>
 */
const AIMessageWrapper = styled(Box)`
  min-width: 95%;
  max-width: 95%;
`;

/**
 * AIChat component for handling AI-powered chat interactions
 * @param {Object} props - Component props
 * @param {Object} props.htmlElementOverrides - Custom HTML element overrides for parsing
 * @returns {JSX.Element|null} The AIChat component or null if disabled
 */
export const AIChat = ({ htmlElementOverrides }) => {
  const workspace = useWorkspace();
  const history = useHistory();
  const { enableAIDashboards } = useWorkspaceFeatureFlags();
  const { user } = useAuth0();
  const userPerson = useUserPerson();

  const {
    canRetryOrEdit,
    editingMessageId,
    messages,
    previousMessages,
    complete,
    setUserHasScrolledUp,
    userHasScrolledUp,
    chatId,
    setQuestion,
    question,
    onRetry,
    onEditLastMessage,
    onSendQuestion,
    isThinking,
    onSubmitMessageFeedback,
  } = useAIChat();

  const [pendingSend, setPendingSend] = React.useState(false);

  useEffect(() => {
    if (pendingSend && question) {
      onSendQuestion();
      setPendingSend(false);
    }
  }, [pendingSend, question, onSendQuestion]);

  const handlePromptSelect = React.useCallback(
    (prompt) => {
      setQuestion(prompt);
      setPendingSend(true);
    },
    [setQuestion]
  );

  /** @type {Object} Scroll-to-bottom hook interface */
  const { scrollContainerRef, scrollToBottom, handleScroll } =
    useScrollToBottom({
      threshold: 100,
      setUserHasScrolledUp,
      userHasScrolledUp,
    });

  /**
   * Effect for auto-scrolling chat to bottom on new messages
   * @effect
   * @description Automatically scrolls to bottom when new content arrives,
   * but respects user's manual scroll position
   * @param {Array<Object>} messages - Current messages array
   * @param {Array<Object>} previousMessages - Previous messages for comparison
   * @param {boolean} userHasScrolledUp - Whether user scrolled up manually
   * @performance Uses requestAnimationFrame for smooth scrolling
   */
  useEffect(() => {
    if (messages.length > 0) {
      /** @type {boolean} Whether new content has been added */
      const hasNewContent =
        JSON.stringify(messages) !== JSON.stringify(previousMessages);

      /** @type {boolean} Whether we should automatically scroll */
      const shouldAutoScroll = hasNewContent && !userHasScrolledUp;

      if (shouldAutoScroll) {
        requestAnimationFrame(scrollToBottom);
      }
    }
  }, [messages, previousMessages, userHasScrolledUp, scrollToBottom]);

  return (
    <Box
      borderRadius={2}
      width="100%"
      height="100%"
      display="flex"
      flexDirection="column"
    >
      <Box
        ref={scrollContainerRef}
        flex="1 1 auto"
        display="flex"
        flexDirection="column"
        width="100%"
        overflowY="auto"
        minHeight={0}
        padding="8px"
        onScroll={handleScroll}
      >
        {(() => {
          /**
           * Renders welcome message when chat is empty and complete
           * @returns {JSX.Element} Welcome message component
           */
          if (messages.length === 0 && complete) {
            // Use Person's display name from DB, fallback to auth0 username
            const personName = userPerson ? getName(userPerson) : null;
            const userName =
              personName || user?.given_name || user?.nickname || "there";

            return (
              <AIChatWelcome
                userName={userName}
                onPromptSelect={handlePromptSelect}
                showSuggestions={!question}
              />
            );
          }

          /**
           * Renders grouped messages with appropriate styling and components
           * @returns {Array<JSX.Element>} Array of rendered message components
           */
          return messages
            .map((message, groupIndex) => {
              /**
               * Render assistant message with AI styling
               * @returns {JSX.Element} Assistant message component
               */
              if (message.role === "assistant") {
                const { tools = [] } = message;
                const lastTool = tools[tools.length - 1];

                const isLastAssistantMessage = !messages
                  .slice(groupIndex + 1)
                  .some((m) => m.role === "assistant");
                const isDone = message.status === "done";
                const followUpPrompts =
                  isLastAssistantMessage && isDone
                    ? extractFollowUpPrompts(message.content)
                    : [];

                // Strip <follow-up> blocks from the displayed content so that
                // marked doesn't render their text content inline before
                // html-react-parser can suppress them via HTMLElementOverrides.
                const displayContent = stripFollowUpTags(message.content);

                return (
                  <Flex
                    flexDirection="column"
                    justifyContent="flex-start"
                    mt="4px"
                    mb="4px"
                    gap="8px"
                    key={`assistant-${groupIndex}`}
                  >
                    {lastTool && (
                      <AIMessageWrapper>
                        <ToolMessage
                          message={lastTool}
                          allToolsCalled={tools}
                        />
                      </AIMessageWrapper>
                    )}

                    <AIMessageWrapper>
                      <AIMessage
                        complete={isDone}
                        content={displayContent || lastTool?.thought}
                        options={createHtmlParsingOptions(
                          workspace.id,
                          chatId,
                          history,
                          htmlElementOverrides,
                          enableAIDashboards,
                          !isDone
                        )}
                        showAIWarning={false}
                        ariaLabel="AI Response"
                      />
                      <Flex justifyContent="flex-end">
                        <MessageFeedback
                          messageId={message.id}
                          initialRating={message.feedbackRating}
                          initialComment={message.feedbackComment}
                          onSubmitFeedback={onSubmitMessageFeedback}
                          disabled={!complete}
                        />
                      </Flex>
                    </AIMessageWrapper>

                    {complete && followUpPrompts.length > 0 && (
                      <Flex flexWrap="wrap" gap="xs">
                        {followUpPrompts.map(({ label, prompt }) => (
                          <PromptButton
                            key={prompt}
                            variant="ghost"
                            small
                            disabled={!complete}
                            onClick={() => handlePromptSelect(prompt)}
                          >
                            {label}
                            &nbsp;&nbsp;
                            {prompt.includes("/dashboard") && (
                              <Icon
                                icon={["fa", "chart-line"]}
                                color="greyDarker"
                              />
                            )}
                          </PromptButton>
                        ))}
                      </Flex>
                    )}
                  </Flex>
                );
              } else if (message.role === "tool") {
                /**
                 * Render tool message with count indicator
                 * @returns {JSX.Element} Tool message component
                 */
                const toolMessage = {
                  role: "tool",
                  content: message.content,
                  complete: message.complete,
                  count: message.count,
                };
                return (
                  <Flex
                    justifyContent="flex-start"
                    mt="4px"
                    mb="4px"
                    key={`tool-group-${groupIndex}`}
                  >
                    <AIMessageWrapper>
                      <ToolMessage
                        message={toolMessage}
                        allToolsCalled={[toolMessage]}
                      />
                    </AIMessageWrapper>
                  </Flex>
                );
              } else if (message.role === "user") {
                /**
                 * Render user message with right alignment
                 * @returns {JSX.Element} User message component
                 */
                const isLastUserMessage = !messages
                  .slice(groupIndex + 1)
                  .some((m) => m.role === "user");
                const showRetryButton = isLastUserMessage && canRetryOrEdit;

                return (
                  <Flex
                    flexDirection="column"
                    alignItems="flex-end"
                    justifyContent="flex-end"
                    mt="4px"
                    mb="4px"
                    gap="4px"
                    key={`user-${groupIndex}`}
                  >
                    <Box
                      background="white"
                      color="greyDarkest"
                      padding="6px 8px"
                      borderRadius={2}
                      maxWidth="95%"
                      border="1px solid"
                      borderColor="greyLight"
                      boxShadow="0 2px 8px rgba(0,0,0,0.1)"
                      style={{ wordWrap: "break-word" }}
                    >
                      {parseHTMLToReact(
                        message.content,
                        createHtmlParsingOptions(
                          workspace.id,
                          chatId,
                          history,
                          htmlElementOverrides,
                          enableAIDashboards,
                          false
                        )
                      )}
                    </Box>
                    {showRetryButton && (
                      <Flex gap="s" alignItems="center">
                        <Popover
                          text="Retry"
                          direction="top"
                          width="fit-content"
                        >
                          <Button
                            type="button"
                            variant="ghost"
                            iconOnly
                            small
                            height="24px"
                            width="24px"
                            onClick={onRetry}
                            ariaLabel="Retry"
                          >
                            <Icon icon={["fas", "redo"]} color="greyDarker" />
                          </Button>
                        </Popover>
                        <Popover
                          text="Edit"
                          direction="top"
                          width="fit-content"
                        >
                          <Button
                            type="button"
                            variant="ghost"
                            iconOnly
                            small
                            height="24px"
                            width="24px"
                            onClick={onEditLastMessage}
                            ariaLabel="Edit"
                            disabled={!!editingMessageId}
                          >
                            <Icon icon={["fas", "pen"]} color="greyDarker" />
                          </Button>
                        </Popover>
                      </Flex>
                    )}
                  </Flex>
                );
              } else {
                /**
                 * Unknown message role - skip rendering
                 * @returns {null}
                 */
                return null;
              }
            })
            .filter(Boolean);
        })()}
        {isThinking && (
          <Flex
            flexDirection="column"
            justifyContent="flex-start"
            mt="4px"
            mb="4px"
            gap="8px"
            key="thinking-message"
          >
            <AIMessageWrapper>
              <AIMessage
                complete={false}
                content="Thinking... "
                options={createHtmlParsingOptions(
                  workspace.id,
                  chatId,
                  history,
                  htmlElementOverrides,
                  enableAIDashboards,
                  false
                )}
                showAIWarning={false}
              />
            </AIMessageWrapper>
          </Flex>
        )}
      </Box>
    </Box>
  );
};

/**
 * PropTypes validation for AIChat component
 * @type {Object}
 * @property {any} [htmlElementOverrides] - Optional custom HTML element overrides
 */
AIChat.propTypes = {
  htmlElementOverrides: PropTypes.any,
};
